import { useSwipeable } from "react-swipeable";
import { FC, useState, useCallback } from "react";
import { color as themeColor } from "@styles";
import styled from "styled-components";
import { RewardTab } from ".";
import { RewardCard } from "../../atoms/RewardCard";

export const RewardCardTabSwitcherMobile: FC<
  React.PropsWithChildren<{
    rewards: RewardTab["rewards"];
    backgroundColor?: string;
  }>
> = ({ rewards, backgroundColor }) => {
  const [isSwiping, setIsSwiping] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);

  const handleClick = useCallback(
    (index: number) => {
      if (index <= 0) {
        setCurrentIndex(0);
      } else if (index >= rewards.length - 1) {
        setCurrentIndex(rewards.length - 1);
      } else {
        setCurrentIndex(index);
      }
    },
    [rewards, setCurrentIndex],
  );

  const goLeft = useCallback(() => {
    setIsSwiping(true);
    handleClick(currentIndex - 1);
    setTimeout(() => {
      setIsSwiping(false);
    }, 400);
  }, [currentIndex, setIsSwiping, handleClick]);

  const goRight = useCallback(() => {
    setIsSwiping(true);
    handleClick(currentIndex + 1);
    setTimeout(() => {
      setIsSwiping(false);
    }, 400);
  }, [currentIndex, setIsSwiping, handleClick]);

  const swipeHandlers = useSwipeable({
    onSwipedLeft: () => goRight(),
    onSwipedRight: () => goLeft(),
    trackMouse: true,
    touchEventOptions: { passive: true },
  });

  return (
    <Container backgroundColor={backgroundColor} {...swipeHandlers}>
      <Wrapper tabIndex={currentIndex}>
        {rewards.map((reward) => {
          return (
            <RewardCardWrapper key={reward.heading}>
              <StyledRewardCard {...reward} />
            </RewardCardWrapper>
          );
        })}
      </Wrapper>
      <LeftArrow isSwiping={isSwiping} onClick={goLeft} disabled={currentIndex === 0}>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="10"
          height="18"
          viewBox="0 0 10 18"
          fill="none"
        >
          <path
            d="M9 1L1 9L9 17"
            stroke="#956AFF"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </LeftArrow>
      <RightArrow
        isSwiping={isSwiping}
        onClick={goRight}
        disabled={currentIndex === rewards.length - 1}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="10"
          height="18"
          viewBox="0 0 10 18"
          fill="none"
        >
          <path
            d="M1 17L9 9L0.999999 1"
            stroke="#956AFF"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </RightArrow>
    </Container>
  );
};

const IMAGE_WIDTH = 0.88; // %
const IMAGE_MARGIN = 0.33; // %

const Arrow = styled.div<{ isSwiping?: boolean; disabled?: boolean }>`
  height: 24rem;
  margin: 2rem 0;
  background: transparent;
  opacity: ${(p) => (p.disabled ? 0 : 0.5)};
  width: 5rem;
  position: absolute;
  cursor: pointer;
  bottom: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  user-select: none;
  transition: ${(p) => (p.isSwiping ? `100ms ease-out` : `400ms ease-out`)};

  &:hover {
    opacity: ${(p) => (p.disabled ? 0 : 1)};
  }
`;

const LeftArrow = styled(Arrow)`
  left: 0;
`;

const RightArrow = styled(Arrow)`
  right: 0;
`;

const Wrapper = styled.div<{ tabIndex: number }>`
  display: flex;
  padding-top: 1.5rem;
  padding-bottom: 2rem;
  white-space: nowrap;
  transition: 200ms ease-in-out;
  ${(p) =>
    !p.tabIndex
      ? null
      : `transform: translateX(calc(-${p.tabIndex * IMAGE_WIDTH * 100}vw - ${
          p.tabIndex * IMAGE_MARGIN * 2
        }rem))`};
  cursor: pointer;
  user-select: none;

  &:focus {
    border: none;
    outline: none;
  }
`;

const StyledRewardCard = styled(RewardCard)`
  width: ${IMAGE_WIDTH * 100}%;
`;

const RewardCardWrapper = styled.div`
  flex: 0 0 ${IMAGE_WIDTH * 100}vw;
  margin: 0 ${IMAGE_MARGIN}rem;

  &:first-of-type {
    margin-left: ${((1 - IMAGE_WIDTH) / 2) * 100}%;
  }
  &:last-of-type {
    margin-right: ${((1 - IMAGE_WIDTH) / 2) * 100}%;
  }
`;

const Container = styled.div<{ backgroundColor?: string }>`
  position: "relative",
  background: ${(p) => p.backgroundColor || themeColor.S310};
`;
